<template>
  <div class="answer-preview-page" :style="pageStyle">
    <!-- 顶部导航栏 -->
    <div class="preview-header">
      <el-button v-if="!isExportSource" icon="el-icon-back" @click="handleBack">返回</el-button>
      <span class="header-title">答卷详情</span>
      <span class="header-info">
        提交时间：{{ answerInfo.submitTime || '-' }}
      </span>
    </div>
    
    <!-- 主内容区域 -->
    <div class="preview-content">
      <div class="form-main-card" :style="cardStyle">
        <!-- 问卷标题 -->
        <div class="form-header">
          <h1 class="form-title">{{ formTitle }}</h1>
          <div class="form-desc" v-if="formDesc" v-html="formDesc"></div>
        </div>
        
        <!-- 经销商信息（如果有） -->
        <div class="dealer-info" v-if="answerInfo.dealerCode || answerInfo.dealerName">
          <div class="info-item" v-if="answerInfo.dealerCode">
            <span class="label">经销商编码：</span>
            <span class="value">{{ answerInfo.dealerCode }}</span>
          </div>
          <div class="info-item" v-if="answerInfo.dealerName">
            <span class="label">经销商名称：</span>
            <span class="value">{{ answerInfo.dealerName }}</span>
          </div>
        </div>
        
        <!-- 问题列表 -->
        <div class="questions-list">
          <div 
            v-for="(item, index) in formItems" 
            :key="item.itemKey" 
            class="question-item"
          >
            <div class="question-header">
              <span class="question-index">{{ item.serialNo || (index + 1) }}.</span>
              <span class="question-required" v-if="item.required">*</span>
              <span class="question-label" v-html="item.label"></span>
            </div>
            
            <!-- 答案显示区域 -->
            <div class="answer-display">
              <!-- 选择题 -->
              <template v-if="isChoiceQuestion(item.itemType) && !isMingjianQuestion(item.itemType)">
                <div 
                  v-for="opt in item.options" 
                  :key="opt.value" 
                  class="option-item"
                  :class="{ 'is-selected': isSelected(item.itemKey, opt.value) }"
                >
                  <span class="option-marker">
                    <i v-if="isSelected(item.itemKey, opt.value)" class="el-icon-check"></i>
                    <span v-else class="option-circle"></span>
                  </span>
                  <span class="option-text" v-html="opt.label"></span>
                </div>
              </template>
              
              <!-- 文件/图片上传题 -->
              <template v-else-if="!isMingjianQuestion(item.itemType) && isFileJsonValue(getAnswerText(item.itemKey))">
                <div class="file-answer">
                  <template v-for="(file, fIdx) in parseFileValue(getAnswerText(item.itemKey))">
                    <!-- 图片文件 -->
                    <div v-if="isImageFile(file)" :key="'img-'+fIdx" class="media-preview-item">
                      <el-image 
                        :src="file.url" 
                        style="width: 100px; height: 100px; border-radius: 6px;"
                        fit="cover"
                        :preview-src-list="[file.url]"
                      >
                        <div slot="error" class="image-error">
                          <i class="el-icon-picture-outline"></i>
                          <span>加载失败</span>
                        </div>
                      </el-image>
                      <span class="media-name">{{ file.name || '图片' }}</span>
                      <a :href="file.url" target="_blank" class="media-download" title="新窗口打开">
                        <i class="el-icon-link"></i>
                      </a>
                      <!-- 拍照定位信息 -->
                      <div v-if="file.location" class="photo-location">
                        <div class="location-address"><i class="el-icon-location-outline"></i> {{ file.location.address }}</div>
                        <div class="location-time" v-if="file.location.time"><i class="el-icon-time"></i> {{ file.location.time }}</div>
                        <div class="location-coords" v-if="file.location.lng && file.location.lat">{{ file.location.lng.toFixed(6) }}, {{ file.location.lat.toFixed(6) }}</div>
                      </div>
                    </div>
                    <!-- 视频文件 -->
                    <div v-else-if="isVideoFile(file)" :key="'video-'+fIdx" class="media-preview-item video-item">
                      <video 
                        :src="file.url" 
                        controls 
                        preload="metadata"
                        class="video-player"
                      ></video>
                      <span class="media-name">{{ file.name || '视频' }}</span>
                      <a :href="file.url" target="_blank" class="media-download" title="下载">
                        <i class="el-icon-download"></i>
                      </a>
                    </div>
                    <!-- 音频文件 -->
                    <div v-else-if="isAudioFile(file)" :key="'audio-'+fIdx" class="media-preview-item audio-item">
                      <audio :src="file.url" controls preload="metadata" class="audio-player"></audio>
                      <span class="media-name">{{ file.name || '音频' }}</span>
                      <a :href="file.url" target="_blank" class="media-download" title="下载">
                        <i class="el-icon-download"></i>
                      </a>
                    </div>
                    <!-- 普通文件：显示链接 -->
                    <div v-else :key="'file-'+fIdx" class="file-item">
                      <i :class="getFileIcon(file.name)"></i>
                      <a :href="file.url" target="_blank" class="file-link">{{ file.name || '文件' }}</a>
                      <a :href="file.url" download class="file-download" title="下载">
                        <i class="el-icon-download"></i>
                      </a>
                    </div>
                  </template>
                </div>
              </template>
              
              <!-- 直接URL格式的媒体文件（如录音） -->
              <template v-else-if="isDirectMediaUrl(getAnswerText(item.itemKey))">
                <div class="file-answer">
                  <div v-if="isDirectAudioUrl(getAnswerText(item.itemKey))" class="media-preview-item audio-item">
                    <audio :src="getAnswerText(item.itemKey)" controls preload="metadata" class="audio-player"></audio>
                    <span class="media-name">录音</span>
                    <a :href="getAnswerText(item.itemKey)" target="_blank" class="media-download" title="下载">
                      <i class="el-icon-download"></i>
                    </a>
                  </div>
                  <div v-else-if="isDirectVideoUrl(getAnswerText(item.itemKey))" class="media-preview-item video-item">
                    <video :src="getAnswerText(item.itemKey)" controls preload="metadata" class="video-player"></video>
                    <span class="media-name">视频</span>
                  </div>
                  <div v-else-if="isDirectImageUrl(getAnswerText(item.itemKey))" class="media-preview-item">
                    <el-image :src="getAnswerText(item.itemKey)" style="width: 100px; height: 100px; border-radius: 6px;" fit="cover" :preview-src-list="[getAnswerText(item.itemKey)]"></el-image>
                  </div>
                </div>
              </template>
              
              <!-- 热力图题 -->
              <template v-else-if="isHeatmapQuestion(item.itemType)">
                <div class="heatmap-answer">
                  <HeatmapQuestion
                    :question="getHeatmapQuestionData(item)"
                    :value="getAnswerText(item.itemKey)"
                    :readonly="true"
                  />
                </div>
              </template>
              
              <!-- 廉政协议题 -->
              <template v-else-if="isIntegrityAgreement(item.itemType)">
                <div class="agreement-answer">
                  <div v-if="parseAgreementAnswer(item.itemKey).agreementUrl" class="agreement-pdf-pages" :ref="'pdfContainer_' + item.itemKey">
                    <div v-if="pdfStates[item.itemKey] === 'loading'" class="pdf-loading"><i class="el-icon-loading"></i> PDF 加载中...</div>
                    <div v-if="pdfStates[item.itemKey] === 'error'" class="pdf-error"><i class="el-icon-warning-outline"></i> PDF 加载失败</div>
                  </div>
                  <div class="agreement-check-status">
                    <i :class="parseAgreementAnswer(item.itemKey).checked ? 'el-icon-success' : 'el-icon-warning'" 
                       :style="{ color: parseAgreementAnswer(item.itemKey).checked ? '#67c23a' : '#e6a23c' }"></i>
                    <span>{{ parseAgreementAnswer(item.itemKey).checked ? '已确认同意廉政协议' : '未确认' }}</span>
                  </div>
                </div>
              </template>
              
              <!-- 明检分类题型 -->
              <template v-else-if="isMingjianQuestion(item.itemType)">
                <div class="mingjian-preview">
                  <!-- 拍照图片（明检-拍照题） -->
                  <div v-if="isMingjianPhotoQuestion(item.itemType) && getMingjianPhotos(item.itemKey).length > 0" class="mingjian-photos-section">
                    <div class="mingjian-label"><i class="el-icon-camera"></i> 拍照取证</div>
                    <div class="file-answer">
                      <div v-for="(photo, pIdx) in getMingjianPhotos(item.itemKey)" :key="'mjp-'+pIdx" class="media-preview-item">
                        <el-image :src="photo.url" style="width: 100px; height: 100px; border-radius: 6px;" fit="cover" :preview-src-list="getMingjianPhotos(item.itemKey).map(p => p.url)" :initial-index="pIdx">
                          <div slot="error" class="image-error"><i class="el-icon-picture-outline"></i></div>
                        </el-image>
                      </div>
                    </div>
                  </div>
                  <!-- 文件附件（明检-文件题，始终显示，未上传给出占位） -->
                  <div v-if="isMingjianFileQuestion(item.itemType)" class="mingjian-files-section">
                    <div class="mingjian-label"><i class="el-icon-paperclip"></i> 附件</div>
                    <template v-if="getMingjianFiles(item.itemKey).length > 0">
                      <!-- 图片类：缩略图 -->
                      <div v-if="mingjianImageFiles(item.itemKey).length > 0" class="file-answer">
                        <div v-for="(img, iIdx) in mingjianImageFiles(item.itemKey)" :key="'mjfi-'+iIdx" class="media-preview-item">
                          <el-image :src="img.url" style="width: 100px; height: 100px; border-radius: 6px;" fit="cover" :preview-src-list="mingjianImageFiles(item.itemKey).map(f => f.url)" :initial-index="iIdx">
                            <div slot="error" class="image-error"><i class="el-icon-picture-outline"></i></div>
                          </el-image>
                        </div>
                      </div>
                      <!-- 非图片类：文件行 -->
                      <div v-for="(f, fIdx) in mingjianNonImageFiles(item.itemKey)" :key="'mjfn-'+fIdx" class="mingjian-file-row">
                        <i :class="['mingjian-file-icon', getFileIcon(f.name)]"></i>
                        <div class="mingjian-file-info">
                          <div class="mingjian-file-name">{{ f.name }}</div>
                          <div class="mingjian-file-meta">{{ formatPreviewFileSize(f.size) }}</div>
                        </div>
                        <a :href="f.url" target="_blank" rel="noopener" class="mingjian-file-action" title="下载"><i class="el-icon-download"></i></a>
                      </div>
                    </template>
                    <div v-else class="mingjian-files-empty">
                      <i class="el-icon-folder-opened"></i>
                      <span>未上传附件</span>
                    </div>
                  </div>
                  <!-- 分类结果 -->
                  <div class="mingjian-result-section">
                    <div class="mingjian-label"><i class="el-icon-s-grid"></i> 分类结果</div>
                    <div v-if="item.itemType === 'MINGJIAN_PHOTO_TEXT'" class="text-answer">
                      {{ getMingjianTextValue(item.itemKey) || '未填写' }}
                    </div>
                    <div v-else>
                      <div v-for="opt in item.options" :key="opt.value" class="option-item" :class="{ 'is-selected': isMingjianOptionSelected(item.itemKey, opt.value) }">
                        <span class="option-marker">
                          <i v-if="isMingjianOptionSelected(item.itemKey, opt.value)" class="el-icon-check"></i>
                          <span v-else class="option-circle"></span>
                        </span>
                        <span class="option-text" v-html="opt.label"></span>
                      </div>
                    </div>
                  </div>
                  <!-- "其他"补充文本 -->
                  <div v-if="getMingjianOtherText(item.itemKey)" style="margin-top: 6px; padding: 6px 10px; background: #fdf6ec; border-radius: 4px; font-size: 13px; color: #e6a23c;">
                    <i class="el-icon-edit"></i> 其他补充：{{ getMingjianOtherText(item.itemKey) }}
                  </div>
                </div>
              </template>
              
              <!-- 文本题 -->
              <template v-else>
                <div class="text-answer">
                  {{ getAnswerText(item.itemKey) || '未作答' }}
                </div>
              </template>
            </div>
          </div>
        </div>
        
        <!-- 底部信息 -->
        <div class="form-footer" v-if="answerInfo.durationSeconds > 0 || (answerInfo.totalScore !== undefined && answerInfo.totalScore > 0)">
          <div class="footer-info">
            <span>答题用时：{{ formatDuration(answerInfo.durationSeconds) }}</span>
            <span v-if="answerInfo.totalScore !== undefined">得分：{{ answerInfo.totalScore }}</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'index/axios'
import HeatmapQuestion from 'survey/views/fill/questions/HeatmapQuestion.vue'
import { parseAgreementAnswer as _parseAgreement, renderPdfToContainer } from '@/utils/agreementPdf'

export default {
  name: 'AnswerPreviewPage',
  components: { HeatmapQuestion },
  
  data() {
    return {
      loading: false,
      formTitle: '',
      formDesc: '',
      formItems: [],
      answerMap: {},
      answerInfo: {},
      formItemsRaw: [], // 保存原始题目数据用于热力图
      pdfStates: {} // PDF渲染状态 { itemKey: 'loading'|'done'|'error' }
    }
  },
  
  computed: {
    formKey() {
      return this.$route.query.key || ''
    },
    answerKey() {
      return this.$route.query.answerKey || ''
    },
    pageStyle() {
      return {
        background: '#f5f7fa',
        minHeight: '100vh'
      }
    },
    cardStyle() {
      return {
        background: '#fff',
        borderRadius: '8px',
        padding: '30px 40px',
        maxWidth: '800px',
        margin: '0 auto'
      }
    },
    isExportSource() {
      return this.$route.query.source === 'export'
    }
  },
  
  created() {
    if (this.formKey && this.answerKey) {
      this.loadData()
    }
  },
  
  methods: {
    handleBack() {
      if (window.history.length > 1) {
        this.$router.back()
      } else {
        this.$router.push('/project')
      }
    },
    
    async loadData() {
      this.loading = true
      try {
        // 使用公开 API（无需登录）
        const [formRes, detailRes] = await Promise.all([
          axios.get(`/public/form/${this.formKey}`),
          axios.get(`/public/form/answer/preview/${this.answerKey}`)
        ])
        
        // 处理问卷题目（公开 API 返回完整问卷，包含 items 字段）
        const formData = formRes?.data?.data || formRes?.data || {}
        const itemsData = formData.items || []
        this.formTitle = formData.name || '问卷详情'
        this.formDesc = formData.description || ''
        this.formItemsRaw = itemsData
        const tagToItemType = {
          'mingjian-photo-radio': 'MINGJIAN_PHOTO_RADIO',
          'mingjian-photo-checkbox': 'MINGJIAN_PHOTO_CHECKBOX',
          'mingjian-photo-text': 'MINGJIAN_PHOTO_TEXT',
          'mingjian-file-radio': 'MINGJIAN_FILE_RADIO',
          'mingjian-file-checkbox': 'MINGJIAN_FILE_CHECKBOX'
        }
        this.formItems = itemsData
          .filter(item => !this.isPaginationItem(item.itemType))
          .map(item => {
            let itemType = item.itemType || item.item_type
            // 防御：后端 itemType 若与 config.tag 不一致，以 config.tag 为准
            try {
              const cfg = JSON.parse(item.configJson || item.config_json || '{}')
              const cfgTag = cfg.tag || cfg.__config__?.tag
              if (cfgTag && tagToItemType[cfgTag] && itemType !== tagToItemType[cfgTag]) {
                itemType = tagToItemType[cfgTag]
              }
            } catch (e) {}
            return {
              itemKey: item.itemKey || item.item_key,
              itemType,
              label: item.label || item.title,
              serialNo: item.serialNo || item.serial_no || '',
              required: item.required,
              options: item.options || [],
              configJson: item.configJson || item.config_json
            }
          })
        
        // 处理答卷详情
        const answer = detailRes?.data?.data || detailRes?.data || {}
        console.log('[AnswerPreview] answer detail:', answer)
        
        if (answer) {
          this.answerInfo = {
            submitTime: answer.submitTime,
            durationSeconds: answer.durationSeconds,
            totalScore: answer.totalScore,
            dealerCode: answer.dealerCode || answer.DealerCode,
            dealerName: answer.dealerName || answer.DealerName
          }
          
          // 构建答案映射
          if (answer.answers && Array.isArray(answer.answers)) {
            answer.answers.forEach(ans => {
              this.answerMap[ans.itemKey] = ans.value
            })
            console.log('[AnswerPreview] answerMap:', this.answerMap)
          }
        }
        
      } catch (e) {
        console.error('加载数据失败', e)
        this.$message.error('加载数据失败')
      } finally {
        this.loading = false
      }
      
      // 渲染廉政协议PDF
      this.$nextTick(() => this.renderAgreementPdfs())
    },
    
    isPaginationItem(type) {
      return ['PAGINATION', 'PAGE_BREAK', 'DIVIDER', 'DESCRIPTION'].includes(type)
    },
    
    isChoiceQuestion(type) {
      return ['RADIO', 'CHECKBOX', 'SINGLE_CHOICE', 'MULTIPLE_CHOICE', 'SELECT', 'MINGJIAN_PHOTO_RADIO', 'MINGJIAN_PHOTO_CHECKBOX', 'MINGJIAN_FILE_RADIO', 'MINGJIAN_FILE_CHECKBOX'].includes(type)
    },
    
    isSelected(itemKey, optionValue) {
      const answer = this.answerMap[itemKey]
      if (!answer && answer !== 0) return false
      
      try {
        const selected = JSON.parse(answer)
        if (Array.isArray(selected)) {
          return selected.some(v => String(v) === String(optionValue))
        }
        return String(selected) === String(optionValue)
      } catch {
        return String(answer) === String(optionValue)
      }
    },
    
    getAnswerText(itemKey) {
      return this.answerMap[itemKey] || ''
    },
    
    formatDuration(seconds) {
      if (!seconds) return '00:00:00'
      const h = Math.floor(seconds / 3600)
      const m = Math.floor((seconds % 3600) / 60)
      const s = seconds % 60
      return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`
    },

    // 检测值是否为文件JSON格式
    isFileJsonValue(value) {
      if (!value || typeof value !== 'string') return false
      const trimmed = value.trim()
      if (!trimmed.startsWith('[') && !trimmed.startsWith('{')) return false
      if (!trimmed.includes('"url"')) return false
      try {
        const parsed = JSON.parse(trimmed)
        if (Array.isArray(parsed) && parsed.length > 0 && parsed[0] && parsed[0].url) return true
        if (parsed && typeof parsed === 'object' && !Array.isArray(parsed) && parsed.url) return true
      } catch (e) {
        return false
      }
      return false
    },

    // 解析文件上传的值
    parseFileValue(value) {
      if (!value) return []
      if (Array.isArray(value)) return value
      try {
        const parsed = JSON.parse(value)
        if (Array.isArray(parsed)) return parsed
        if (parsed && typeof parsed === 'object') return [parsed]
        return [{ url: String(parsed), name: '文件' }]
      } catch (e) {
        if (typeof value === 'string' && value.startsWith('http')) {
          return [{ url: value, name: '文件' }]
        }
        return []
      }
    },

    // 检测文件是否为图片
    isImageFile(file) {
      if (!file) return false
      const url = file.url || ''
      const name = file.name || ''
      const imageExts = ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.bmp', '.svg']
      const lowerUrl = url.toLowerCase()
      const lowerName = name.toLowerCase()
      return imageExts.some(ext => lowerUrl.includes(ext) || lowerName.endsWith(ext))
    },

    // 检测文件是否为视频
    isVideoFile(file) {
      if (!file) return false
      const url = file.url || ''
      const name = file.name || ''
      const videoExts = ['.mp4', '.webm', '.ogg', '.mov', '.avi', '.mkv', '.m4v']
      const lowerUrl = url.toLowerCase()
      const lowerName = name.toLowerCase()
      return videoExts.some(ext => lowerUrl.includes(ext) || lowerName.endsWith(ext))
    },

    // 检测文件是否为音频
    isAudioFile(file) {
      if (!file) return false
      const url = file.url || ''
      const name = file.name || ''
      const audioExts = ['.mp3', '.wav', '.ogg', '.aac', '.m4a', '.flac', '.wma', '.amr', '.webm']
      const lowerUrl = url.toLowerCase()
      const lowerName = name.toLowerCase()
      
      const hasAudioExt = audioExts.some(ext => lowerUrl.includes(ext) || lowerName.endsWith(ext))
      if (!hasAudioExt) return false
      
      // 如果是 .webm，通过文件名关键词区分音频和视频
      if (lowerUrl.includes('.webm') || lowerName.endsWith('.webm')) {
        return lowerUrl.includes('recording') || lowerUrl.includes('audio') || 
               lowerName.includes('recording') || lowerName.includes('audio') ||
               lowerName.includes('录音')
      }
      
      return true
    },

    // 根据文件名获取对应图标
    getFileIcon(fileName) {
      if (!fileName) return 'el-icon-document'
      const ext = fileName.toLowerCase().split('.').pop()
      const iconMap = {
        pdf: 'el-icon-document',
        doc: 'el-icon-document',
        docx: 'el-icon-document',
        xls: 'el-icon-s-grid',
        xlsx: 'el-icon-s-grid',
        ppt: 'el-icon-s-data',
        pptx: 'el-icon-s-data',
        zip: 'el-icon-folder-opened',
        rar: 'el-icon-folder-opened',
        '7z': 'el-icon-folder-opened',
        txt: 'el-icon-tickets'
      }
      return iconMap[ext] || 'el-icon-document'
    },
    
    // 检测是否为直接URL格式的媒体文件
    isDirectMediaUrl(value) {
      if (!value || typeof value !== 'string') return false
      const trimmed = value.trim()
      if (!trimmed.startsWith('http')) return false
      if (trimmed.startsWith('[') || trimmed.startsWith('{')) return false
      const lowerUrl = trimmed.toLowerCase()
      const mediaExts = ['.mp3', '.wav', '.ogg', '.aac', '.m4a', '.webm', '.mp4', '.mov', '.jpg', '.jpeg', '.png', '.gif', '.webp']
      return mediaExts.some(ext => lowerUrl.includes(ext))
    },
    
    // 检测是否为直接URL格式的音频文件
    isDirectAudioUrl(value) {
      if (!value || typeof value !== 'string') return false
      const lowerUrl = value.toLowerCase()
      const audioExts = ['.mp3', '.wav', '.ogg', '.aac', '.m4a', '.flac', '.wma', '.amr']
      if (audioExts.some(ext => lowerUrl.includes(ext))) return true
      if (lowerUrl.includes('.webm')) {
        return lowerUrl.includes('recording') || lowerUrl.includes('audio') || lowerUrl.includes('录音')
      }
      return false
    },
    
    // 检测是否为直接URL格式的视频文件
    isDirectVideoUrl(value) {
      if (!value || typeof value !== 'string') return false
      const lowerUrl = value.toLowerCase()
      const videoExts = ['.mp4', '.mov', '.avi', '.mkv', '.m4v']
      if (videoExts.some(ext => lowerUrl.includes(ext))) return true
      if (lowerUrl.includes('.webm')) {
        return !lowerUrl.includes('recording') && !lowerUrl.includes('audio') && !lowerUrl.includes('录音')
      }
      return false
    },
    
    // 检测是否为直接URL格式的图片文件
    isDirectImageUrl(value) {
      if (!value || typeof value !== 'string') return false
      const lowerUrl = value.toLowerCase()
      const imageExts = ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.bmp', '.svg']
      return imageExts.some(ext => lowerUrl.includes(ext))
    },
    
    // 检测是否为热力图题
    isHeatmapQuestion(itemType) {
      return itemType === 'HEATMAP'
    },
    
    // 获取热力图题目数据（转换为 HeatmapQuestion 组件需要的格式）
    getHeatmapQuestionData(item) {
      return {
        item_key: item.itemKey,
        config_json: item.configJson,
        configJson: item.configJson
      }
    },
    
    // 检测是否为廉政协议题
    isIntegrityAgreement(itemType) {
      return itemType === 'INTEGRITY_AGREEMENT'
    },
    
    // 解析廉政协议答案
    parseAgreementAnswer(itemKey) {
      return _parseAgreement(this.answerMap[itemKey])
    },
    
    // 渲染所有廉政协议PDF
    async renderAgreementPdfs() {
      for (const item of this.formItems) {
        if (item.itemType !== 'INTEGRITY_AGREEMENT') continue
        const { agreementUrl } = this.parseAgreementAnswer(item.itemKey)
        if (!agreementUrl) continue
        
        const refKey = 'pdfContainer_' + item.itemKey
        const container = this.$refs[refKey]?.[0] || this.$refs[refKey]
        if (!container) continue
        
        await renderPdfToContainer(agreementUrl, container, state => {
          this.$set(this.pdfStates, item.itemKey, state)
        })
      }
    },
    
    // 明检分类题型辅助方法
    isMingjianPhotoQuestion(type) {
      return ['MINGJIAN_PHOTO_RADIO', 'MINGJIAN_PHOTO_CHECKBOX', 'MINGJIAN_PHOTO_TEXT'].includes(type)
    },
    isMingjianFileQuestion(type) {
      return ['MINGJIAN_FILE_RADIO', 'MINGJIAN_FILE_CHECKBOX'].includes(type)
    },
    isMingjianQuestion(type) {
      return this.isMingjianPhotoQuestion(type) || this.isMingjianFileQuestion(type)
    },
    getMingjianPhotos(itemKey) {
      const val = this.answerMap[itemKey]
      if (!val) return []
      try {
        const parsed = typeof val === 'string' ? JSON.parse(val) : val
        return parsed.photos || []
      } catch { return [] }
    },
    getMingjianFiles(itemKey) {
      const val = this.answerMap[itemKey]
      if (!val) return []
      try {
        const parsed = typeof val === 'string' ? JSON.parse(val) : val
        if (Array.isArray(parsed.files)) return parsed.files
        if (parsed.file) return [parsed.file]
        return []
      } catch { return [] }
    },
    mingjianImageFiles(itemKey) {
      return this.getMingjianFiles(itemKey).filter(f => {
        const name = (f && (f.name || f.url) || '').toLowerCase()
        return /\.(jpg|jpeg|png|gif|bmp|webp)(\?|$)/.test(name)
      })
    },
    mingjianNonImageFiles(itemKey) {
      return this.getMingjianFiles(itemKey).filter(f => {
        const name = (f && (f.name || f.url) || '').toLowerCase()
        return !/\.(jpg|jpeg|png|gif|bmp|webp)(\?|$)/.test(name)
      })
    },
    getMingjianFile(itemKey) {
      const arr = this.getMingjianFiles(itemKey)
      return arr.length > 0 ? arr[0] : null
    },
    formatPreviewFileSize(bytes) {
      if (!bytes) return ''
      if (bytes < 1024) return bytes + ' B'
      if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB'
      if (bytes < 1024 * 1024 * 1024) return (bytes / 1024 / 1024).toFixed(1) + ' MB'
      return (bytes / 1024 / 1024 / 1024).toFixed(2) + ' GB'
    },
    getMingjianTextValue(itemKey) {
      const val = this.answerMap[itemKey]
      if (!val) return ''
      try {
        const parsed = typeof val === 'string' ? JSON.parse(val) : val
        return parsed.textValue || ''
      } catch { return String(val) }
    },
    getMingjianOtherText(itemKey) {
      const val = this.answerMap[itemKey]
      if (!val) return ''
      try {
        const parsed = typeof val === 'string' ? JSON.parse(val) : val
        return parsed.otherText || ''
      } catch { return '' }
    },
    isMingjianOptionSelected(itemKey, optionValue) {
      const val = this.answerMap[itemKey]
      if (!val) return false
      try {
        const parsed = typeof val === 'string' ? JSON.parse(val) : val
        // 单选
        if (parsed.selectedValue !== undefined) {
          return String(parsed.selectedValue) === String(optionValue)
        }
        // 多选
        if (Array.isArray(parsed.selectedValues)) {
          return parsed.selectedValues.map(String).includes(String(optionValue))
        }
        return false
      } catch { return false }
    }
  }
}
</script>

<style lang="scss" scoped>
.answer-preview-page {
  min-height: 100vh;
}

.preview-header {
  position: sticky;
  top: 0;
  z-index: 100;
  display: flex;
  align-items: center;
  padding: 12px 20px;
  background: #fff;
  border-bottom: 1px solid #e4e7ed;
  
  .header-title {
    font-size: 16px;
    font-weight: 600;
    margin-left: 20px;
    color: #303133;
  }
  
  .header-info {
    margin-left: auto;
    color: #909399;
    font-size: 14px;
  }
}

.preview-content {
  padding: 30px 20px;
}

.form-main-card {
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
}

.form-header {
  text-align: center;
  margin-bottom: 30px;
  padding-bottom: 20px;
  border-bottom: 1px solid #f0f0f0;
  
  .form-title {
    font-size: 24px;
    color: #303133;
    margin: 0 0 10px 0;
  }
  
  .form-desc {
    color: #909399;
    font-size: 14px;
    line-height: 1.6;
  }
}

.dealer-info {
  background: #f5f7fa;
  padding: 15px 20px;
  border-radius: 6px;
  margin-bottom: 20px;
  
  .info-item {
    display: inline-block;
    margin-right: 30px;
    
    .label {
      color: #909399;
    }
    .value {
      color: #303133;
      font-weight: 500;
    }
  }
}

.questions-list {
  .question-item {
    margin-bottom: 25px;
    padding-bottom: 20px;
    border-bottom: 1px dashed #e4e7ed;
    
    &:last-child {
      border-bottom: none;
    }
  }
  
  .question-header {
    margin-bottom: 12px;
    display: flex;
    flex-wrap: wrap;
    align-items: baseline;
    
    .question-index {
      font-weight: 600;
      color: #303133;
      margin-right: 2px;
    }
    
    .question-required {
      color: #F56C6C;
      margin-right: 4px;
    }
    
    .question-label {
      color: #303133;
      line-height: 1.6;
      
      ::v-deep p {
        display: inline;
        margin: 0;
      }
    }
  }
}

.answer-display {
  padding-left: 20px;
  
  .option-item {
    display: flex;
    align-items: center;
    padding: 10px 15px;
    margin-bottom: 8px;
    border-radius: 6px;
    background: #f5f7fa;
    
    &.is-selected {
      background: #ecf5ff;
      border: 1px solid #409EFF;
      
      .option-marker i {
        color: #409EFF;
      }
    }
    
    .option-marker {
      margin-right: 10px;
      
      .option-circle {
        display: inline-block;
        width: 16px;
        height: 16px;
        border: 1px solid #dcdfe6;
        border-radius: 50%;
      }
      
      i {
        font-size: 18px;
        font-weight: bold;
      }
    }
    
    .option-text {
      color: #606266;
    }
  }
  
  .text-answer {
    background: #f5f7fa;
    padding: 15px 20px;
    border-radius: 6px;
    color: #303133;
    line-height: 1.6;
    min-height: 40px;
  }

  /* 文件列表样式 */
  .file-answer {
    display: flex;
    flex-wrap: wrap;
    gap: 12px;
  }

  .file-item {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 10px 16px;
    background: #f5f7fa;
    border: 1px solid #e4e7ed;
    border-radius: 6px;
    
    i {
      color: #409EFF;
      font-size: 18px;
    }
  }

  .file-link {
    color: #409EFF;
    text-decoration: none;
    word-break: break-all;
    flex: 1;
    
    &:hover {
      text-decoration: underline;
    }
  }

  .file-download {
    color: #909399;
    margin-left: 8px;
    
    &:hover {
      color: #409EFF;
    }
  }

  /* 媒体预览公共样式 */
  .media-preview-item {
    display: inline-flex;
    flex-direction: column;
    align-items: center;
    gap: 6px;
    position: relative;
    
    .el-image {
      border: 1px solid #e4e7ed;
      overflow: hidden;
      cursor: pointer;
      transition: all 0.2s;
      
      &:hover {
        border-color: #409EFF;
        box-shadow: 0 4px 12px rgba(64, 158, 255, 0.2);
      }
    }
    
    .media-name {
      font-size: 12px;
      color: #909399;
      max-width: 120px;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      text-align: center;
    }
    
    .media-download {
      position: absolute;
      top: 4px;
      right: 4px;
      width: 24px;
      height: 24px;
      background: rgba(0,0,0,0.5);
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      color: #fff;
      font-size: 12px;
      opacity: 0;
      transition: opacity 0.2s;
    }
    
    &:hover .media-download {
      opacity: 1;
    }
    
    .photo-location {
      margin-top: 4px;
      padding: 6px 10px;
      background: #f0f7ff;
      border-radius: 4px;
      font-size: 11px;
      color: #606266;
      max-width: 200px;
      word-break: break-all;
      
      .location-address {
        line-height: 1.4;
        i { color: #e6a23c; margin-right: 2px; }
      }
      .location-time {
        margin-top: 2px;
        i { color: #909399; margin-right: 2px; }
      }
      .location-coords {
        margin-top: 2px;
        color: #909399;
        font-size: 10px;
      }
    }
  }
  
  /* 图片加载失败状态 */
  .image-error {
    width: 100px;
    height: 100px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    background: #f5f7fa;
    border: 1px dashed #dcdfe6;
    border-radius: 6px;
    color: #909399;
    
    i {
      font-size: 28px;
      margin-bottom: 4px;
    }
    
    span {
      font-size: 12px;
    }
  }

  /* 视频播放器 */
  .video-item {
    .video-player {
      width: 200px;
      max-height: 150px;
      border-radius: 6px;
      background: #000;
    }
  }

  /* 音频播放器 */
  .audio-item {
    .audio-player {
      width: 250px;
      height: 40px;
    }
  }
}

.form-footer {
  margin-top: 30px;
  padding-top: 20px;
  border-top: 1px solid #f0f0f0;
  
  .footer-info {
    display: flex;
    justify-content: center;
    gap: 30px;
    color: #909399;
    font-size: 14px;
  }
}

/* 廉政协议答案 */
.agreement-answer {
  padding: 12px 16px;
  background: #f5f7fa;
  border-radius: 6px;
  
  .agreement-pdf-pages {
    width: 100%;
    border: 1px solid #ebeef5;
    border-radius: 8px;
    overflow: hidden;
    padding: 4px;
    background: #f5f5f5;
    margin-bottom: 10px;
    .pdf-loading, .pdf-error {
      padding: 24px; text-align: center; color: #909399; font-size: 14px;
      i { margin-right: 6px; }
    }
    .pdf-error { color: #e6a23c; }
  }
  
  .agreement-check-status {
    display: flex;
    align-items: center;
    gap: 6px;
    
    i { font-size: 16px; }
    span { font-size: 14px; color: #606266; }
  }
}

/* 明检分类题型预览 */
.mingjian-preview {
  .mingjian-photos-section { margin-bottom: 12px; }
  .mingjian-files-section { margin-bottom: 12px; }
  .mingjian-result-section { margin-top: 8px; }
  .mingjian-label {
    font-size: 13px;
    font-weight: 500;
    color: #303133;
    margin-bottom: 8px;
    display: flex;
    align-items: center;
    gap: 4px;
    i { color: #409EFF; }
  }
  .mingjian-file-row {
    display: flex; align-items: center; gap: 10px;
    padding: 10px 12px; border: 1px solid #e4e7ed; border-radius: 8px; background: #fff;
  }
  .mingjian-file-icon { font-size: 22px; color: #409EFF; flex-shrink: 0; }
  .mingjian-file-info { flex: 1; min-width: 0; display: flex; flex-direction: column; gap: 4px; }
  .mingjian-file-name { font-size: 13px; color: #303133; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .mingjian-file-meta { font-size: 12px; color: #909399; }
  .mingjian-file-action { color: #606266; font-size: 16px; cursor: pointer; padding: 4px; text-decoration: none; }
  .mingjian-file-action:hover { color: #409EFF; }
  .mingjian-files-empty {
    display: flex; align-items: center; gap: 8px;
    padding: 16px; border: 1px dashed #dcdfe6; border-radius: 8px;
    color: #909399; font-size: 13px; background: #fafbfc; justify-content: center;
  }
  .mingjian-files-empty i { font-size: 18px; }
}
</style>
